import { clearAuthHeader } from '../services/api';
import toast from 'react-hot-toast';
import { clearPreservedBookingState } from './sessionManager';
import { setSessionExpiredNoticePending } from './sessionExpiredNoticeFlags';
import { clearRememberMeAuthForUser } from './rememberMe';

// Comprehensive logout utility
export const clearAllUserData = () => {
  // Get current user ID before clearing
  let userId = 'guest';
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    userId = user.id || 'guest';
  } catch (error) {
    console.error('Failed to get user ID:', error);
  }

  // DON'T clear currency preferences - users want their selection to persist
  // The currency preference should remain even after logout
  // Only the cache is cleared, not the user's selected currency

  // Clear authentication data
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('user');

  // Remove only this user from remembered accounts; others stay remembered
  clearRememberMeAuthForUser(userId);
  
  // Dispatch custom event to notify currency context of user change
  window.dispatchEvent(new CustomEvent('userChanged', { detail: { userId: null } }));

  // Clear 2FA session data
  sessionStorage.removeItem('twofa_email');
  sessionStorage.removeItem('twofa_password');
  sessionStorage.removeItem('twofa_questions');
  sessionStorage.removeItem('twofa_phone');

  // Clear any session storage
  sessionStorage.removeItem('redirectAfterLogin');
  sessionStorage.removeItem('bookingData');
  sessionStorage.removeItem('flightSearchData');
  sessionStorage.removeItem('selectedFlight');
  sessionStorage.removeItem('passengers');
  sessionStorage.removeItem('paymentData');

  // Preserved booking state is kept on session expiry (cleared on manual logout via clearPreservedBookingState)

  // Clear user-specific passenger form data
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (
      key.startsWith(`passenger_form_data_${userId}_`) ||
      key.startsWith('passenger_form_data_') // Clean up old non-user-specific keys too
    )) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => sessionStorage.removeItem(key));

  // Clear any other app-specific data
  sessionStorage.removeItem('searchHistory');
  sessionStorage.removeItem('userPreferences');
  sessionStorage.removeItem('recentSearches');
  sessionStorage.removeItem('savedFlights');
  sessionStorage.removeItem('bookingHistory');

  // Clear any cached API data
  sessionStorage.removeItem('airportCache');
  sessionStorage.removeItem('countryCache');
  sessionStorage.removeItem('flightCache');

  // Clear API authorization header
  clearAuthHeader();
};

// Simple logout that can be called from anywhere (including API interceptors)
export const performSimpleLogout = async () => {
  // Preserve payment callback URL before clearing session storage
  // so user can complete their booking after re-login
  const paymentCallbackUrl = sessionStorage.getItem('redirectAfterLogin');
  const isPaymentCallback = paymentCallbackUrl && paymentCallbackUrl.includes('payment-callback');

  if (sessionStorage.getItem('token')) {
    try {
      const api = (await import('../services/api')).default;
      const { buildLogoutPushBody } = await import('../services/pushService');
      await api.post('/auth/logout', buildLogoutPushBody());
    } catch {
      // Session expiry path — best effort only
    }
  }

  // Clear all user data
  clearAllUserData();

  // Restore payment callback URL if it was set
  if (isPaymentCallback) {
    sessionStorage.setItem('redirectAfterLogin', paymentCallbackUrl);
  }

  setSessionExpiredNoticePending();

  // Redirect to home page (modal opens on load via App.js)
  window.location.href = '/';
};

// Enhanced logout function that can be used throughout the app
export const performLogout = async (dispatch, navigate) => {
  try {
    // Import the logout action dynamically to avoid circular dependencies
    const { logoutUser } = await import('../store/slices/authSlice');

    // Dispatch logout action (this will also clear sessionStorage)
    await dispatch(logoutUser()).unwrap();

    // Clear any additional data
    clearAllUserData();
    clearPreservedBookingState();

    // Navigate to home page
    if (navigate) {
      navigate('/', { replace: true });
    }

    // Show success toast
    // toast.success('You have been logged out successfully');

  } catch (error) {
    // Even if API call fails, clear everything locally
    clearAllUserData();
    clearPreservedBookingState();

    if (navigate) {
      navigate('/', { replace: true });
    }

    toast.error('Logout completed (with errors)');
  }
};